import { FC, PropsWithChildren, ReactNode } from 'react';

import { Layout, NextButton, StepHeader } from '@eneco/flows/components';
import { Fields } from '@eneco/flows/types';
import { Grid, Stack } from '@eneco/sparky';
import { FieldValues, UseFormHandleSubmit } from 'react-hook-form';

interface Props {
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  textualData?: Fields;
  nextButtonLabel?: string;
  isLoading?: boolean;
  showNextButton?: boolean;
  /**
   * Content-width preset forwarded to `Layout.Content`. These control only the
   * max-width of the column: `A` ≈ 400px (the default narrow, centered step),
   * `B` ≈ 600px, `C` ≈ 1000px, `D` ≈ 1000px with each inner block centered.
   * Use a wider preset (e.g. `C`) when rendering an `aside`, since the narrow
   * default cannot fit a form next to a side panel.
   */
  variant?: 'A' | 'B' | 'C' | 'D';
  /**
   * Optional side panel rendered next to the form on large screens (and beneath
   * it on small screens). Supplying it switches the shell to a two-column layout.
   */
  aside?: ReactNode;
  /**
   * Content rendered after the next button (e.g. a chat activator). Keep this
   * after the primary action so tab order stays fields → Next → extra actions.
   */
  footer?: ReactNode;
}

/**
 * The `Layout.Content` + `StepHeader` + `NextButton` shell repeated by every
 * energyFlow step screen. Step-specific fields go in `children`, between the
 * header and the next button.
 *
 * By default the shell is a single, centered column. Passing an `aside` switches
 * it to a two-column layout — form on the left, side panel on the right — which
 * also wants a wider `variant` (e.g. `C`).
 */
export const StepShell: FC<PropsWithChildren<Props>> = ({
  handleSubmit,
  textualData,
  nextButtonLabel,
  isLoading,
  showNextButton = true,
  variant = 'A',
  aside,
  footer,
  children,
}) => {
  const body = (
    <>
      {children}
      {showNextButton && <NextButton isLoading={isLoading}>{nextButtonLabel}</NextButton>}
      {footer}
    </>
  );

  return (
    <Layout.Content variant={variant} handleSubmit={handleSubmit}>
      <StepHeader textualData={textualData} />
      {aside ? (
        // The header spans the full width above the grid, so the side panel
        // top-aligns with the first form field rather than the step title.
        <Grid
          gridTemplateColumns={{ initial: '1fr', lg: 'minmax(0, 1fr) 360px' }}
          gap={{ initial: '8', lg: '10' }}
          alignY="start">
          <Stack direction="column" gap="6">
            {body}
          </Stack>
          <aside>{aside}</aside>
        </Grid>
      ) : (
        body
      )}
    </Layout.Content>
  );
};
